import { useCallback, useEffect, useMemo, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { Archive, Edit3, FilterX, Info, Plus, Tags } from 'lucide-react'
import {
  archiveLabel,
  createLabel,
  getLabels,
  updateLabel,
  type Label,
  type LabelCategory,
  type LabelFilters,
  type LabelStatus
} from '../api/labels'
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog'
import { useToast } from '../components/ToastProvider'
import { Button, DataTableElement, DataTableSurface, EmptyState, IconButton, Pill, RowActions, SearchField, StatusBadge } from '../components/ui'
import { LabelDialog } from '../features/labels/LabelDialog'
import { labelCategoryLabels, labelCategoryOptions, labelStatusLabels, labelStatusOptions } from '../features/labels/labelDisplay'
import type { AdminOutletContext } from '../layouts/AdminLayout'
import styles from './LabelsPage.module.css'

const defaultLabelCategory: LabelCategory = 'genre'
const emptyLabelFilters: LabelFilters = {
  category: defaultLabelCategory,
  status: 'active',
  search: undefined
}

const cx = (...classNames: Array<string | false | null | undefined>): string =>
  classNames
    .filter(Boolean)
    .map((className) => styles[className as string] ?? className)
    .join(' ')

const createEmptyLabel = (category: LabelCategory): Label => ({
  id: '',
  category,
  name: '',
  slug: '',
  description: '',
  aliases: [],
  status: 'active',
  track_count: 0,
  created_at: '',
  updated_at: ''
})

export const LabelsPage = () => {
  const { toast } = useToast()
  const { closeMobileActions, setMobileMenuItems } = useOutletContext<AdminOutletContext>()
  const [filters, setFilters] = useState<LabelFilters>(emptyLabelFilters)
  const [labels, setLabels] = useState<Label[]>([])
  const [selectedLabel, setSelectedLabel] = useState<Label | null>(null)
  const [labelToArchive, setLabelToArchive] = useState<Label | null>(null)
  const [openInfoLabelId, setOpenInfoLabelId] = useState<string | null>(null)
  const [dictionaryLabels, setDictionaryLabels] = useState<Label[]>([])
  const [archiving, setArchiving] = useState(false)
  const selectedCategory = filters.category ?? defaultLabelCategory

  const openCreateLabelDialog = useCallback((): void => {
    setSelectedLabel(createEmptyLabel(selectedCategory))
  }, [selectedCategory])

  const loadDictionaryLabels = useCallback(async (): Promise<void> => {
    const payload = await getLabels()
    setDictionaryLabels(payload)
  }, [])

  const loadLabels = useCallback(async (nextFilters = filters): Promise<void> => {
    const payload = await getLabels(nextFilters)
    setLabels(payload)
  }, [filters])

  useEffect(() => {
    setMobileMenuItems(
      <button
        className="mobile-actions-item"
        onClick={() => {
          closeMobileActions()
          openCreateLabelDialog()
        }}
        role="menuitem"
        type="button"
      >
        <Plus size={17} />
        <span>Create label</span>
      </button>
    )

    return () => setMobileMenuItems(null)
  }, [closeMobileActions, openCreateLabelDialog, setMobileMenuItems])

  useEffect(() => {
    void Promise.all([
      loadDictionaryLabels(),
      loadLabels()
    ])
  }, [loadDictionaryLabels, loadLabels])

  useEffect(() => {
    if (!openInfoLabelId) {
      return undefined
    }

    const closeInfoPopover = (event: PointerEvent): void => {
      const target = event.target

      if (target instanceof Element && target.closest('.label-actions-cell')) {
        return
      }

      setOpenInfoLabelId(null)
    }

    document.addEventListener('pointerdown', closeInfoPopover)

    return () => {
      document.removeEventListener('pointerdown', closeInfoPopover)
    }
  }, [openInfoLabelId])

  const applyFilters = (patch: LabelFilters): void => {
    const nextFilters = {
      ...filters,
      ...patch
    }
    setFilters(nextFilters)
    void loadLabels(nextFilters)
  }

  const clearFilters = (): void => {
    const nextFilters: LabelFilters = {
      category: selectedCategory,
      status: 'active',
      search: undefined
    }

    setFilters(nextFilters)
    void loadLabels(nextFilters)
  }

  const categoryCounts = useMemo(() => {
    return labelCategoryOptions.reduce<Record<LabelCategory, number>>((counts, category) => {
      counts[category] = dictionaryLabels.filter((label) => label.category === category).length
      return counts
    }, {} as Record<LabelCategory, number>)
  }, [dictionaryLabels])

  const activeCount = useMemo(
    () => dictionaryLabels.filter((label) => label.status === 'active').length,
    [dictionaryLabels]
  )
  const archivedCount = useMemo(
    () => dictionaryLabels.filter((label) => label.status === 'archived').length,
    [dictionaryLabels]
  )
  const usedCount = useMemo(
    () => dictionaryLabels.filter((label) => label.track_count > 0).length,
    [dictionaryLabels]
  )
  const selectedCategoryTotal = categoryCounts[selectedCategory] ?? 0
  const hasNarrowingFilters = Boolean(filters.search) || filters.status !== 'active'

  const confirmArchive = async (): Promise<void> => {
    if (!labelToArchive) {
      return
    }

    setArchiving(true)
    try {
      await archiveLabel(labelToArchive.id)
      toast(`Archived label "${labelToArchive.name}".`)
      setLabelToArchive(null)
      await loadDictionaryLabels()
      await loadLabels(filters)
    } finally {
      setArchiving(false)
    }
  }

  return (
    <>
      <div className={cx('page-header', 'labels-page-header')}>
        <div>
          <h1 className="page-title">Manage Labels</h1>
          <div className={cx('labels-inline-summary')} aria-label="Label dictionary summary">
            <span>{dictionaryLabels.length} labels</span>
            <span>{activeCount} active</span>
            <span>{archivedCount} archived</span>
            <span>{usedCount} used</span>
          </div>
        </div>
        <Button className={cx('label-create-button')} onClick={openCreateLabelDialog} variant="primary">
          <Plus size={16} /> Create label
        </Button>
      </div>

      <section className={cx('labels-control-bar')} aria-label="Label controls">
        <div className={cx('labels-category-nav')} aria-label="Label categories">
          {labelCategoryOptions.map((category) => (
            <button
              className={cx('labels-category-button', category === selectedCategory && 'active')}
              key={category}
              onClick={() => applyFilters({ category })}
              type="button"
            >
              <span>{labelCategoryLabels[category]}</span>
              <strong>{categoryCounts[category] ?? 0}</strong>
            </button>
          ))}
        </div>
        <div className={cx('labels-filter-row')}>
          <span className={cx('labels-current-category')}>
            {labelCategoryLabels[selectedCategory]} <strong>{labels.length}/{selectedCategoryTotal}</strong>
          </span>
          <SearchField
            className={cx('labels-search-field')}
            onChange={(event) => applyFilters({ search: event.target.value })}
            placeholder="Search labels..."
            value={filters.search ?? ''}
          />
          <select
            className="form-input select-input"
            onChange={(event) => applyFilters({ status: event.target.value ? event.target.value as LabelStatus : undefined })}
            value={filters.status ?? ''}
          >
            <option value="">All statuses</option>
            {labelStatusOptions.map((status) => (
              <option key={status} value={status}>{labelStatusLabels[status]}</option>
            ))}
          </select>
          {hasNarrowingFilters && (
            <Button className={cx('labels-clear-button')} onClick={clearFilters}>
              <FilterX size={16} /> Clear
            </Button>
          )}
        </div>
      </section>

      <DataTableSurface className={cx('labels-table-card')} fullWidth mobileStack mobileVariant="entityList">
        <DataTableElement>
          <thead>
            <tr>
              <th>Name</th>
              <th>Aliases</th>
              <th>Tracks</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {labels.map((label) => {
              const infoOpen = openInfoLabelId === label.id

              return (
                <tr key={label.id}>
                  <td className="group-title-cell label-title-cell" data-label="">
                    <span className="group-title-line">
                      <strong>{label.name}</strong>
                      <Pill className={cx('label-slug-pill')} tone="muted">{label.slug}</Pill>
                      <StatusBadge
                        className={cx('label-title-status-badge')}
                        label={labelStatusLabels[label.status]}
                        tone={label.status === 'active' ? 'ready' : 'default'}
                      />
                    </span>
                    {label.description && <span>{label.description}</span>}
                  </td>
                  <td className="group-secondary-cell" data-label="Aliases">
                    <div className={styles.chips}>
                      {label.aliases.slice(0, 3).map((alias) => (
                        <Pill key={alias}>{alias}</Pill>
                      ))}
                      {label.aliases.length > 3 && <span className="muted">+{label.aliases.length - 3}</span>}
                      {label.aliases.length === 0 && <span className="muted">No aliases</span>}
                    </div>
                  </td>
                  <td className="group-secondary-cell" data-label="Tracks"><Pill strong>{label.track_count}</Pill></td>
                  <td className="group-secondary-cell label-status-cell" data-label="Status">
                    <StatusBadge
                      label={labelStatusLabels[label.status]}
                      tone={label.status === 'active' ? 'ready' : 'default'}
                    />
                  </td>
                  <td className="group-actions-cell label-actions-cell" data-label="Actions">
                    <RowActions className="label-mobile-actions" mobileStack>
                      <IconButton
                        aria-expanded={infoOpen}
                        icon={<Info size={15} />}
                        label={`Label info for ${label.name}`}
                        onClick={() => setOpenInfoLabelId((current) => current === label.id ? null : label.id)}
                        title="Label info"
                      />
                    </RowActions>
                    <RowActions className="label-inline-actions" mobileStack>
                      <IconButton
                        icon={<Edit3 size={15} />}
                        label={`Edit ${label.name}`}
                        onClick={() => setSelectedLabel(label)}
                        title="Edit"
                      />
                      <IconButton
                        disabled={label.status === 'archived'}
                        icon={<Archive size={15} />}
                        label={`Archive ${label.name}`}
                        onClick={() => setLabelToArchive(label)}
                        title="Archive"
                      />
                    </RowActions>
                    {infoOpen && (
                      <div className="track-info-popover label-info-popover" role="dialog" aria-label={`Details for ${label.name}`}>
                        <div>
                          <span>Category</span>
                          <strong>{labelCategoryLabels[label.category]}</strong>
                        </div>
                        <div>
                          <span>Tracks</span>
                          <strong>{label.track_count}</strong>
                        </div>
                        <div>
                          <span>Aliases</span>
                          <strong>{label.aliases.length > 0 ? label.aliases.join(', ') : 'No aliases'}</strong>
                        </div>
                        <div className="track-info-actions">
                          <IconButton
                            icon={<Edit3 size={15} />}
                            label={`Edit ${label.name}`}
                            onClick={() => {
                              setOpenInfoLabelId(null)
                              setSelectedLabel(label)
                            }}
                            title="Edit"
                          />
                          <IconButton
                            disabled={label.status === 'archived'}
                            icon={<Archive size={15} />}
                            label={`Archive ${label.name}`}
                            onClick={() => {
                              setOpenInfoLabelId(null)
                              setLabelToArchive(label)
                            }}
                            title="Archive"
                          />
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </DataTableElement>
        {labels.length === 0 && (
          <EmptyState>
            <Tags size={24} />
            <span>No labels match these filters.</span>
          </EmptyState>
        )}
      </DataTableSurface>

      {selectedLabel && (
        <LabelDialog
          label={selectedLabel}
          onClose={() => setSelectedLabel(null)}
          onSave={async (input) => {
            if (input.id) {
              await updateLabel(input)
              toast(`Updated label "${input.name}".`)
            } else {
              await createLabel(input)
              toast(`Created label "${input.name}".`)
            }

            setSelectedLabel(null)
            await loadDictionaryLabels()
            await loadLabels(filters)
          }}
        />
      )}

      {labelToArchive && (
        <DeleteConfirmDialog
          description="This will archive the label so it is no longer used for new assignments by default. Existing track labels stay in place."
          isDeleting={archiving}
          itemName={labelToArchive.name}
          onCancel={() => setLabelToArchive(null)}
          onConfirm={confirmArchive}
          title="Archive Label"
        />
      )}
    </>
  )
}
